/**
 * Study Mode Zustand Store
 * State management for Study Mode app
 */

import {create} from 'zustand';
import {StudyDocument, ChatMessage} from '@voxapilot/shared';

interface StudyStore {
  // Current document
  currentDocument: StudyDocument | null;
  setCurrentDocument: (doc: StudyDocument | null) => void;

  // Chat state
  chatHistory: ChatMessage[];
  activeChatId: string | null;
  addChatMessage: (message: ChatMessage) => void;
  setChatHistory: (messages: ChatMessage[]) => void;
  setActiveChatId: (chatId: string | null) => void;
  clearChat: () => void;

  // UI state
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  viewMode: 'documents' | 'chat' | 'flashcards' | 'analytics';
  setViewMode: (mode: 'documents' | 'chat' | 'flashcards' | 'analytics') => void;

  // Document list
  documents: StudyDocument[];
  setDocuments: (docs: StudyDocument[]) => void;
  addDocument: (doc: StudyDocument) => void;
  removeDocument: (docId: string) => void;
  updateDocument: (docId: string, updates: Partial<StudyDocument>) => void;

  // Loading states
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

export const useStudyStore = create<StudyStore>((set) => ({
  // Current document
  currentDocument: null,
  setCurrentDocument: (doc) => set({currentDocument: doc}),

  // Chat state
  chatHistory: [],
  activeChatId: null,
  addChatMessage: (message) =>
    set((state) => ({
      chatHistory: [...state.chatHistory, message],
    })),
  setChatHistory: (messages) => set({chatHistory: messages}),
  setActiveChatId: (chatId) => set({activeChatId: chatId}),
  clearChat: () => set({chatHistory: [], activeChatId: null}),

  // UI state
  sidebarOpen: true,
  setSidebarOpen: (open) => set({sidebarOpen: open}),
  viewMode: 'documents',
  setViewMode: (mode) => set({viewMode: mode}),

  // Document list
  documents: [],
  setDocuments: (docs) => set({documents: docs}),
  addDocument: (doc) =>
    set((state) => ({
      documents: [doc, ...state.documents],
    })),
  removeDocument: (docId) =>
    set((state) => ({
      documents: state.documents.filter((d) => d.id !== docId),
      currentDocument: state.currentDocument?.id === docId ? null : state.currentDocument,
    })),
  updateDocument: (docId, updates) =>
    set((state) => ({
      documents: state.documents.map((d) => (d.id === docId ? {...d, ...updates} : d)),
      currentDocument:
        state.currentDocument?.id === docId
          ? {...state.currentDocument, ...updates}
          : state.currentDocument,
    })),

  // Loading states
  isLoading: false,
  setIsLoading: (loading) => set({isLoading: loading}),
}));

